import { useId, useState, useEffect, type ComponentProps, type ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { FieldLabelRow } from './field-label-row'
import { Input } from '../../ui/input'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip'
import { UploadIcon, CropIcon, ExternalLinkIcon, CameraIcon, ImageIcon, XIcon } from 'lucide-react'
import { ImageEditor } from './-components/image-editor'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

async function optimizeImage(file: File, maxEdge: number, quality = 0.9): Promise<File> {
  if (!file.type.startsWith('image/')) return file
  const probe = await createImageBitmap(file).catch(() => null)
  if (!probe) return file
  const { width, height } = probe
  probe.close()
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return file
  const scale = maxEdge / longest
  const targetW = Math.round(width * scale)
  const targetH = Math.round(height * scale)
  // Downscale during decode: ~65ms, against ~500ms for drawImage on a full-res 12MP bitmap.
  const bitmap = await createImageBitmap(file, {
    resizeWidth: targetW,
    resizeHeight: targetH,
    resizeQuality: 'high',
  }).catch(() => null)
  if (!bitmap) return file
  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    return file
  }
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()
  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
  if (!blob) return file
  return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg', lastModified: Date.now() })
}

export default function ImageField({
  label,
  url,
  className,
  aspectRatio,
  enableCrop = false,
  compact = false,
  variant = 'default',
  uploadLabel,
  optional,
  centerIcon,
  autoOpenEditor = true,
  showRemove = false,
  optimizeOnUpload = false,
  optimizeMaxEdge = 1024,
  ...props
}: ComponentProps<'input'> & {
  label: string
  url?: string
  aspectRatio?: { width: number; height: number }
  enableCrop?: boolean
  compact?: boolean
  /** 'avatar' renders a circular, centered control with a clickable caption. Implies compact layout. */
  variant?: 'default' | 'avatar'
  /** Caption shown under the avatar variant. Defaults to a translated "Upload photo". */
  uploadLabel?: string
  optional?: boolean
  /** Custom node shown in the empty state (compact mode). Defaults to a plain image icon. */
  centerIcon?: ReactNode
  autoOpenEditor?: boolean
  showRemove?: boolean
  optimizeOnUpload?: boolean
  /** Longest-edge target (px) used by optimizeOnUpload. Defaults to 1024. */
  optimizeMaxEdge?: number
}) {
  const { t } = useLingui()
  // The avatar variant is just a styled compact field (circular box + caption).
  const isAvatar = variant === 'avatar'
  if (isAvatar) compact = true
  const field = useFieldContext<File | undefined>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const id = useId()
  const fieldId = `field-${field.name}-${id}-file`
  const [isEditing, setIsEditing] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [originalFile, setOriginalFile] = useState<File | null>(null)
  const [cropArea, setCropArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null)
  const [hasImage, setHasImage] = useState(false)

  // The File always wins over the controlled `url`, so a freshly-picked image survives a remount (back to a wizard
  // step) and a missing server round-trip. Derived during render — in an effect it painted one frame late — and
  // held in state, since a render-time ref read is a React Compiler bail-out. Revocation is the cleanup's, keyed
  // on the URL it was created for.
  const [prevValue, setPrevValue] = useState(field.state.value)
  const [filePreview, setFilePreview] = useState<string | null>(() =>
    field.state.value instanceof File ? URL.createObjectURL(field.state.value) : null,
  )
  if (field.state.value !== prevValue) {
    setPrevValue(field.state.value)
    setFilePreview(field.state.value instanceof File ? URL.createObjectURL(field.state.value) : null)
  }
  useEffect(() => {
    if (!filePreview) return
    return () => URL.revokeObjectURL(filePreview)
  }, [filePreview])

  useEffect(() => {
    if (!field.state.value || typeof field.state.value === 'string') {
      // oxlint-disable-next-line react/set-state-in-effect -- imperative DOM sync: clears the native file input's .value and the <img> src, which can only happen after commit
      setCropArea(null)
      setOriginalFile(null)

      const input = document.getElementById(field.name) as HTMLInputElement | null
      if (input) {
        input.value = ''
      }

      const image = document.getElementById(fieldId) as HTMLImageElement | null
      if (image) {
        image.src = url || ''
      }
      if (!url) setHasImage(false)
    }
  }, [field.state.value, field.name, fieldId, url])

  const content = (
    <div className={cn(compact && 'group/image relative', isAvatar && 'size-28', className)}>
      {label && !compact && (
        <div className="mb-2">
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        </div>
      )}
      <div
        className={cn(
          'text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50 border-input relative rounded-lg border bg-transparent transition-colors',
          compact && 'h-full overflow-hidden',
          isAvatar && '!bg-muted/40 ring-border !rounded-full !border-0 ring-1',
        )}
      >
        <Input
          type="file"
          id={field.name}
          name={field.name}
          accept="image/*"
          aria-invalid={hasFieldError(field.state.meta)}
          ref={(ref) => {
            if (!ref) return
            if (!field.state.value) return
            if (!(field.state.value instanceof File)) return

            // Create FileList so we can restore persisted file on input
            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(field.state.value)
            ref.files = dataTransfer.files

            const image = document.getElementById(fieldId) as HTMLImageElement
            image.src = URL.createObjectURL(field.state.value)
          }}
          onChange={async (event) => {
            const file = event.target.files?.[0]
            if (!file) return

            setCropArea(null)
            setOriginalFile(null)
            setPendingFile(null)

            if (aspectRatio) {
              const img = new Image()
              const objectUrl = URL.createObjectURL(file)

              const handleLoad = async () => {
                URL.revokeObjectURL(objectUrl)

                if (img.width < aspectRatio.width || img.height < aspectRatio.height) {
                  const { width: minWidth, height: minHeight } = aspectRatio
                  const { width: imgWidth, height: imgHeight } = img
                  const errorMsg = t`Image must be at least ${minWidth}x${minHeight} pixels. Uploaded image is ${imgWidth}x${imgHeight} pixels.`

                  field.setMeta((prev) => ({
                    ...prev,
                    errors: [errorMsg],
                    isTouched: true,
                  }))

                  toast.error(t`Image too small`, {
                    description: errorMsg,
                  })

                  event.target.value = ''
                  return
                }

                if (enableCrop && autoOpenEditor) {
                  setOriginalFile(file)
                  setPendingFile(file)
                  setIsEditing(true)
                } else {
                  const finalFile = optimizeOnUpload ? await optimizeImage(file, optimizeMaxEdge) : file
                  if (enableCrop) setOriginalFile(file)
                  field.handleChange(finalFile)
                  const image = document.getElementById(fieldId) as HTMLImageElement
                  if (image) {
                    image.src = URL.createObjectURL(finalFile)
                  }
                }
              }

              const handleError = () => {
                URL.revokeObjectURL(objectUrl)
                const errorMsg = t`Failed to load image. Please try a different file.`

                field.setMeta((prev) => ({
                  ...prev,
                  errors: [errorMsg],
                  isTouched: true,
                }))

                toast.error(t`Invalid image`, {
                  description: errorMsg,
                })

                event.target.value = ''
              }

              img.addEventListener('load', handleLoad)
              img.addEventListener('error', handleError)
              img.src = objectUrl
            } else {
              if (enableCrop && autoOpenEditor) {
                setOriginalFile(file)
                setPendingFile(file)
                setIsEditing(true)
              } else {
                const finalFile = optimizeOnUpload ? await optimizeImage(file, optimizeMaxEdge) : file
                if (enableCrop) setOriginalFile(file)
                field.handleChange(finalFile)
                const image = document.getElementById(fieldId) as HTMLImageElement
                if (image) {
                  image.src = URL.createObjectURL(finalFile)
                }
              }
            }
          }}
          className={
            compact ? 'h-full w-full cursor-pointer opacity-0' : 'h-72 w-full cursor-pointer px-3 py-6 opacity-0'
          }
          {...props}
          disabled={isReadOnly || props.disabled}
          tabIndex={isReadOnly ? -1 : props.tabIndex}
        />

        <img
          id={fieldId}
          alt={label}
          src={filePreview ?? (url === '' ? undefined : url)}
          width="100%"
          height={compact ? '100%' : 288}
          className={cn(
            'pointer-events-none absolute top-0 left-0 hidden w-full object-cover',
            compact ? 'h-full rounded-md' : 'h-72 rounded-md',
            isAvatar && '!rounded-full',
          )}
          onLoad={() => {
            const image = document.getElementById(fieldId) as HTMLImageElement
            image.style.display = 'block'
            setHasImage(true)
          }}
          onError={() => {
            const image = document.getElementById(fieldId) as HTMLImageElement
            image.style.display = ''
            setHasImage(false)
          }}
        />

        <div
          className={
            compact
              ? `pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors ${hasImage ? 'group-hover/image:bg-black/50' : ''}`
              : 'pointer-events-none absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center'
          }
        >
          {compact ? (
            hasImage ? (
              <CameraIcon className="size-5 text-white opacity-0 transition-opacity group-hover/image:opacity-100" />
            ) : (
              (centerIcon ?? <ImageIcon className="text-muted-foreground size-6" />)
            )
          ) : isReadOnly ? null : (
            <div className="bg-background/50 rounded-lg p-2">
              <UploadIcon className="text-foreground size-6" />
            </div>
          )}
        </div>
      </div>

      {showRemove && hasImage && !isReadOnly && (
        <Tooltip>
          <TooltipTrigger
            render={
              <ButtonPrimitive
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  field.handleChange(undefined)
                  setHasImage(false)
                  setOriginalFile(null)
                  setCropArea(null)
                  const input = document.getElementById(field.name) as HTMLInputElement | null
                  if (input) input.value = ''
                  const image = document.getElementById(fieldId) as HTMLImageElement | null
                  if (image) {
                    image.src = url || ''
                    if (!url) image.style.display = ''
                  }
                }}
                className={
                  compact
                    ? 'absolute top-1 left-1 z-10 flex size-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover/image:opacity-100'
                    : 'bg-background/50 text-foreground absolute top-2 right-12 rounded-lg p-2'
                }
              >
                <XIcon className={compact ? 'size-3.5' : 'size-6'} />
              </ButtonPrimitive>
            }
          />
          <TooltipContent>{t`Remove image`}</TooltipContent>
        </Tooltip>
      )}

      {/* Only show external link if we have a URL and haven't changed the image locally */}
      {url && !field.state.value && !compact && (
        <Tooltip>
          <TooltipTrigger
            render={
              <a
                href={url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="bg-background/50 absolute right-2 bottom-2 rounded-lg p-2"
              >
                <ExternalLinkIcon className="text-foreground size-6" />
              </a>
            }
          />
          <TooltipContent>{t`Open image in a new tab`}</TooltipContent>
        </Tooltip>
      )}

      {enableCrop && field.state.value && originalFile && !isReadOnly && (
        <Tooltip>
          <TooltipTrigger
            render={
              <ButtonPrimitive
                type="button"
                aria-label={t`Edit image`}
                onClick={async () => {
                  if (!(field.state.value instanceof File)) return

                  // Always use the original uncropped file for editing
                  const fileToEdit = originalFile

                  if (aspectRatio) {
                    const img = new Image()
                    const objectUrl = URL.createObjectURL(fileToEdit)

                    const handleLoad = () => {
                      URL.revokeObjectURL(objectUrl)

                      if (img.width < aspectRatio.width || img.height < aspectRatio.height) {
                        const { width: minWidth, height: minHeight } = aspectRatio
                        const { width: imgWidth, height: imgHeight } = img
                        const errorMsg = t`Image must be at least ${minWidth}x${minHeight} pixels. Current image is ${imgWidth}x${imgHeight} pixels.`

                        field.setMeta((prev) => ({
                          ...prev,
                          errors: [errorMsg],
                          isTouched: true,
                        }))

                        toast.error(t`Image too small`, {
                          description: errorMsg,
                        })

                        return
                      }

                      setPendingFile(fileToEdit)
                      setIsEditing(true)
                    }

                    const handleError = () => {
                      URL.revokeObjectURL(objectUrl)
                      toast.error(t`Invalid image`, {
                        description: t`Failed to load the current image.`,
                      })
                    }

                    img.addEventListener('load', handleLoad)
                    img.addEventListener('error', handleError)
                    img.src = objectUrl
                  } else {
                    setPendingFile(fileToEdit)
                    setIsEditing(true)
                  }
                }}
                className={
                  compact
                    ? 'absolute top-1 right-1 z-10 flex size-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover/image:opacity-100'
                    : 'bg-background/50 absolute right-2 bottom-2 rounded-lg p-2'
                }
              >
                <CropIcon className={compact ? 'size-3.5' : 'text-foreground size-6'} />
              </ButtonPrimitive>
            }
          />
          <TooltipContent>{t`Edit image`}</TooltipContent>
        </Tooltip>
      )}
      <FieldInfo fieldState={field.state} />

      {enableCrop && (
        <ImageEditor
          open={isEditing}
          file={pendingFile}
          aspectRatio={aspectRatio}
          initialCropArea={cropArea || undefined}
          onSave={(file, newCropArea) => {
            if (!originalFile) {
              setOriginalFile(pendingFile)
            }

            setCropArea(newCropArea)

            // This is what gets submitted to the backend.
            field.handleChange(file)

            const input = document.getElementById(field.name) as HTMLInputElement
            if (input) {
              const dataTransfer = new DataTransfer()
              dataTransfer.items.add(file)
              input.files = dataTransfer.files
            }

            const image = document.getElementById(fieldId) as HTMLImageElement
            if (image) {
              image.src = URL.createObjectURL(file)
            }

            setIsEditing(false)
            setPendingFile(null)
          }}
          onOpenChange={(open) => {
            if (!open) {
              setIsEditing(false)

              if (!field.state.value) {
                const input = document.getElementById(field.name) as HTMLInputElement
                if (input) {
                  input.value = ''
                }
                setPendingFile(null)
                setCropArea(null)
                setOriginalFile(null)
              }
            }
          }}
        />
      )}
    </div>
  )

  if (!isAvatar) return content

  // The caption triggers the same hidden file input (matched by htmlFor → input id).
  return (
    <div className="flex flex-col items-center gap-2">
      {content}
      {!isReadOnly && (
        <label
          htmlFor={field.name}
          className="text-muted-foreground hover:text-foreground cursor-pointer text-xs font-medium transition-colors"
        >
          {uploadLabel ?? t`Upload photo`}
        </label>
      )}
    </div>
  )
}
